import { useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  doc,
  getDoc,
  getFirestore,
  collection,
  addDoc,
  query,
  where,
  orderBy,
  getDocs,
  serverTimestamp,
} from "firebase/firestore";
import { firebaseApp } from "../../firebase";
import NavBar from "../../components/Navigation/NavBar";
import Footer from "../../components/Footer/Footer";
import { XMarkIcon } from "@heroicons/react/24/solid";
import TopAlert from "../../components/Alerts/TopAlert";
import { Helmet } from "react-helmet";
import { ArrowPathIcon } from "@heroicons/react/24/outline";
import FormSubmissionModal from "../../components/Modal/FormSubmissionModal";
import { useAuth } from "../../context/AuthContext";
import MembershipCard from "../Profile/MembershipCard";
import MembershipModal from "../Modal/MembershipDetailsModal";
import { signUpForEvent } from "../../services/firestore/SGloopService";

const FormPageV2 = ({ preview, parentFormData }) => {
  const { id } = useParams();
  const db = getFirestore(firebaseApp);
  const { userData, userRef } = useAuth();
  const [searchParams] = useSearchParams();
  const source = searchParams.get("source");
  const eventId = searchParams.get("eventId");

  // ---------------- Parse source ----------------
  const parseSource = (source) => {
    if (!source) return { mainGroupId: null, subGroupId: null };
    if (source.includes("-")) {
      const [subGroupId, mainGroupId] = source.split("-");
      return { mainGroupId, subGroupId };
    }
    return { mainGroupId: source, subGroupId: null };
  };

  const { mainGroupId, subGroupId } = parseSource(source);

  // ---------------- State ----------------
  const [formData, setFormData] = useState(null);
  const [selected, setSelected] = useState(null);
  const [alert, setAlert] = useState({ message: "", type: "info" });
  const [answers, setAnswers] = useState({});
  const [repeatableCounts, setRepeatableCounts] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [openSubmitFormResponse, setOpenSubmitFormResponse] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userMembership, setUserMembership] = useState(null);
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [sectionHistory, setSectionHistory] = useState([]);

  // ---------------- Load form data ----------------
  useEffect(() => {
    if (parentFormData) {
      setFormData(parentFormData);
    } else {
      const loadForm = async () => {
        const formDoc = await getDoc(doc(db, "forms", id));
        if (formDoc.exists()) setFormData(formDoc.data());
      };
      loadForm();
    }
  }, [id, parentFormData]);

  // ---------------- Fetch membership ----------------
  useEffect(() => {
    const fetchMembership = async () => {
      if (!userData || !formData?.sgloopGroup?.path) return;

      setCurrentUser(userData);

      try {
        const sgloopDocRef = doc(db, formData.sgloopGroup.path);
        const membershipRef = collection(db, `Users/${userData.uid}/membership`);
        const q = query(
          membershipRef,
          where("subSGloopReference", "==", sgloopDocRef),
          orderBy("createdAt", "desc")
        );
        const membershipSnap = await getDocs(q);
        const sgloopGroupSnap = await getDoc(sgloopDocRef);

        if (!membershipSnap.empty) {
          setUserMembership({
            ...membershipSnap.docs[0].data(),
            id: membershipSnap.docs[0].id,
            subSGloopData: sgloopGroupSnap.data(),
          });
        }
      } catch (err) {
        console.error("Error fetching membership:", err);
      }
    };

    fetchMembership();
  }, [userData, formData]);

  // ---------------- Answer handler ----------------
  const handleAnswerChange = (qText, value) => {
    setAnswers((prev) => ({ ...prev, [qText]: value }));
  };

  const addRepeatableSection = (sectionId) => {
    setRepeatableCounts((prev) => ({
      ...prev,
      [sectionId]: (prev[sectionId] || 1) + 1,
    }));
  };

  const removeRepeatableSection = (sectionId, index) => {
    setRepeatableCounts((prev) => {
      const newCount = prev[sectionId] - 1;
      return { ...prev, [sectionId]: newCount > 0 ? newCount : 1 };
    });
  };

  // ---------------- Section Navigation ----------------
  const goNext = () => {
    const currentSection = visibleSections[currentSectionIndex];
    let branchSectionId = null;

    currentSection.questions.forEach((q) => {
      if (q.goToSectionType === "byAnswer" && q.optionsWithDestinations) {
        const key = `${q.question}`;
        const answer = answers[key];
        if (!answer) return;
        const branch = q.optionsWithDestinations.find((o) => o.option === answer);
        if (branch) branchSectionId = branch.goToSection;
      }
    });

    let nextIndex;
    if (branchSectionId) {
      const targetIndex = visibleSections.findIndex((s) => s.id === branchSectionId);
      nextIndex =
        targetIndex !== -1
          ? targetIndex
          : Math.min(currentSectionIndex + 1, visibleSections.length - 1);
    } else {
      nextIndex = Math.min(currentSectionIndex + 1, visibleSections.length - 1);
    }

    setSectionHistory((prev) => [...prev, currentSectionIndex]);
    setCurrentSectionIndex(nextIndex);
  };

  const goPrev = () => {
    setSectionHistory((prev) => {
      if (prev.length === 0) {
        setCurrentSectionIndex(0);
        return [];
      }
      const newHistory = [...prev];
      const lastIndex = newHistory.pop();
      setCurrentSectionIndex(lastIndex);
      return newHistory;
    });
  };

  // ---------------- Submit Handler ----------------
  const handleSubmit = async () => {
  const missingRequired = [];

  visibleSections.forEach((section) => {
    section.questions.forEach((q) => {
      const answer = answers[q.question];
      if (q.required && (!answer || (Array.isArray(answer) && answer.length === 0))) {
        missingRequired.push(q.question);
      }
    });
  });

  if (missingRequired.length > 0) {
    setAlert({
      message: ["Please fill in all required fields:", ...missingRequired].join("\n"),
      type: "error",
    });
    return;
  }

  setSubmitting(true);
  try {
    const responsesRef = collection(db, "forms", id, "responses");
    await addDoc(responsesRef, {
      userUID: currentUser ? currentUser.uid : null,
      answers,
      submittedAt: serverTimestamp(),
    });

    // --- Extract name & email from the answers ---
    const name = answers["Name"];
    const email = answers["Email"];

    // Handle SGloop event signup
    if (eventId && userRef) {
      try {
        await signUpForEvent(
          subGroupId == null ? {} : { subGroupName: "OnesgSubChatBanner" },
          eventId,
          userRef,
          mainGroupId,
          subGroupId
        );
        console.log("Event sign-up completed.");

        // --- CALL YOUR CLOUD FUNCTION ---
        await fetch(
          "https://sendeventemail-wyjrul2qza-as.a.run.app",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name,
              email,
            }),
          }
        );

        console.log("Email function called.");
      } catch (err) {
        console.error("Error during signUpForEvent or email function:", err);
      }
    }

    setAnswers({});
    setOpenSubmitFormResponse(true);
  } catch (err) {
    console.error(err);
    setAlert({ message: "Error submitting form.", type: "error" });
  }
  setSubmitting(false);
};


  if (!formData)
    return (
      <div className="flex items-center justify-center min-h-screen bg-gradient-to-b from-[#8BD1C7] to-[#316391]">
        <div className="flex flex-col items-center">
          <ArrowPathIcon className="h-10 w-10 text-white animate-spin" />
          <p className="mt-4 text-white text-lg font-medium">Loading form...</p>
        </div>
      </div>
    );

  // Filter out sections that members should skip
  const visibleSections = formData.sections.filter(
    (s) => !(userMembership && s.excludeForGroupMembers)
  );

  const currentSection = visibleSections[currentSectionIndex];
  const repeatCount = repeatableCounts[currentSection?.id] || 1;

  const renderQuestions = (questions) =>
    questions.map((q) => {
      const key = `${q.question}`;
      const userAnswer = answers[key] || "";

      const handleChange = (val) => handleAnswerChange(key, val);

      return (
        <div key={key} className="mb-4">
          <label className="block mb-1 font-medium">
            {q.question} {q.required && <span className="text-red-500">*</span>}
          </label>

          {q.type === "shortAnswer" && (
            <input
              type="text"
              value={userAnswer}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-[#316391]"
            />
          )}

          {q.type === "paragraph" && (
            <textarea
              value={userAnswer}
              onChange={(e) => handleChange(e.target.value)}
              className="w-full border rounded px-3 py-2 focus:ring-2 focus:ring-[#316391]"
            />
          )}

          {(q.type === "multipleChoice" || q.type === "checkboxes") &&
            q.options.map((opt) => (
              <label key={opt} className="flex items-center gap-2 mb-1">
                <input
                  type={q.type === "multipleChoice" ? "radio" : "checkbox"}
                  name={`q${q.question}`}
                  value={opt}
                  checked={
                    q.type === "multipleChoice"
                      ? userAnswer === opt
                      : Array.isArray(userAnswer) && userAnswer.includes(opt)
                  }
                  onChange={(e) => {
                    if (q.type === "multipleChoice") handleChange(opt);
                    else {
                      let newArr = Array.isArray(userAnswer) ? [...userAnswer] : [];
                      if (e.target.checked) newArr.push(opt);
                      else newArr = newArr.filter((a) => a !== opt);
                      handleChange(newArr);
                    }
                  }}
                  className="h-4 w-4 text-[#316391] focus:ring-[#8BD1C7]"
                />
                <span>{opt}</span>
              </label>
            ))}
        </div>
      );
    });

  return (
    <>
      <Helmet>
        <title>{formData.title}</title>
        <meta name="description" content={formData.description} />
      </Helmet>

      {openSubmitFormResponse && (
        <FormSubmissionModal
          confirmTitle="Successful Submission"
          confirmSubtitle="Thank you for your submission. A confirmation email will be sent to the email address you provided."
          SGloopGroup={formData?.sgloopGroupData}
          benefits={formData?.sgloopBenefits}
          onClose={() => setOpenSubmitFormResponse(false)}
        />
      )}

      {selected && (
        <MembershipModal membership={selected} onClose={() => setSelected(null)} />
      )}

      <div className="min-h-screen flex flex-col bg-gradient-to-b from-[#8BD1C7] to-[#316391]">
        <NavBar />
        <TopAlert
          message={alert.message}
          type={alert.type}
          duration={4000}
          onClose={() => setAlert({ message: "" })}
        />

        <main className="flex-grow px-4 sm:px-6 md:px-10 pt-28 sm:pt-32 pb-6">
          <div className="max-w-[90%] mx-auto bg-white rounded-lg shadow-lg p-6">
            {formData.bannerUrl && (
              <div className="mb-6">
                <img
                  src={formData.bannerUrl}
                  alt="Form Banner"
                  className="w-full max-h-64 object-contain"
                />
              </div>
            )}

            <h1 className="text-3xl font-bold mb-6">{formData.title}</h1>
            {formData.description && (
              <p className="text-gray-700 mb-6 whitespace-pre-line">{formData.description}</p>
            )}

            {userMembership && (
              <MembershipCard
                key={userMembership.id}
                membership={userMembership}
                onClick={() => setSelected(userMembership)}
              />
            )}

            {currentSection && (
              <div className="mb-6 p-3 rounded bg-gray-50">
                {currentSection.title && (
                  <h2 className="text-lg font-semibold mb-3">{currentSection.title}</h2>
                )}
                {currentSection.description && (
                  <p className="text-gray-600 mb-2 whitespace-pre-line">
                    {currentSection.description}
                  </p>
                )}

                {[...Array(repeatCount)].map((_, ri) => (
                  <div key={ri} className="mb-4 border p-2 rounded bg-white relative">
                    {currentSection.type === "repeatable" && repeatCount > 1 && (
                      <button
                        onClick={() => removeRepeatableSection(currentSection.id, ri)}
                        className="absolute top-2 right-2 text-gray-500 hover:text-red-500"
                        title="Remove this section"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    )}

                    {renderQuestions(currentSection.questions, ri)}
                  </div>
                ))}

                {currentSection.type === "repeatable" && (
                  <button
                    onClick={() => addRepeatableSection(currentSection.id)}
                    className="mt-2 px-3 py-1.5 bg-gray-200 rounded hover:bg-gray-300 text-sm flex items-center gap-1"
                  >
                    + Add
                  </button>
                )}
              </div>
            )}

            {!preview && (
              <div className="mt-6 flex justify-between">
                <button
                  onClick={goPrev}
                  disabled={currentSectionIndex === 0}
                  className="px-6 py-2 bg-gray-400 text-white rounded hover:bg-gray-500 disabled:opacity-50"
                >
                  Previous
                </button>

                {currentSectionIndex === visibleSections.length - 1 ? (
                  <button
                    onClick={handleSubmit}
                    disabled={submitting}
                    className="px-6 py-2 bg-[#316391] text-white rounded hover:bg-[#1f4770] disabled:opacity-50"
                  >
                    {submitting ? "Submitting..." : "Submit"}
                  </button>
                ) : (
                  <button
                    onClick={goNext}
                    className="px-6 py-2 bg-[#316391] text-white rounded hover:bg-[#1f4770]"
                  >
                    Next
                  </button>
                )}
              </div>
            )}
          </div>
        </main>

        <Footer />
      </div>
    </>
  );
};

export default FormPageV2;
